import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import styles from "./MainFileViewer.module.scss";
import { PDFViewerToolbar } from "react-view-pdf";
import { PDFViewerConstants } from "./constants";
import { PdfViewMode } from "../../../../enums/pdfViewModeEnum";

const MainFileView = ({
  url,
  wrapperRef,
}: {
  url: string;
  wrapperRef: HTMLDivElement | null;
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentScale, setCurrentScale] = useState(1.0);
  const [viewMode, setViewMode] = useState<PdfViewMode>(PdfViewMode.DEFAULT);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const pageSizes = useRef<{ width: number; height: number }[]>([]);
  const initialZoomApplied = useRef<boolean>(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const messageComponent = (text: string) => (
    <div className={styles.pdfLoaderWrapper}>
      <p className={styles.pdfLoader}>{text}</p>
    </div>
  );

  const toggleFullScreenView = () => {
    const element = wrapperRef.current;
    if (!element) return;

    if (!document.fullscreenElement) {
      element.requestFullscreen?.().then(() => {
        setFullscreen(true);
        handleToggleViewMode(PdfViewMode.FIT_TO_HEIGHT);
      });
    } else {
      document.exitFullscreen?.().then(() => {
        setFullscreen(false);
        handleToggleViewMode(PdfViewMode.FIT_TO_HEIGHT);
      });
    }
  };

  const zoomToScale = (scale: number) => {
    setCurrentScale(Math.min(Math.max(scale, 0.2), 2.5));
  };

  const zoomToPageView = (mode: PdfViewMode) => {
    const container = fullscreen
      ? (wrapperRef?.current ?? viewerRef.current)
      : viewerRef.current;
    if (!container) return;

    const size = pageSizes.current[currentPage - 1];
    if (!size) return;

    const { width: pageWidth, height: pageHeight } = size;

    switch (mode) {
      case PdfViewMode.FIT_TO_WIDTH: {
        const availableWidth = container.clientWidth;
        zoomToScale(availableWidth / pageWidth);
        break;
      }

      case PdfViewMode.FIT_TO_HEIGHT: {
        const availableHeight = container.clientHeight;
        zoomToScale(availableHeight / pageHeight);
        break;
      }
    }
  };

  const handleToggleViewMode = (mode: PdfViewMode) => {
    setViewMode(mode);
    zoomToPageView(mode);
  };

  const capturePageValues =
    (index: number) =>
    ({ originalWidth, originalHeight }) => {
      pageSizes.current[index] = {
        width: originalWidth,
        height: originalHeight,
      };
    };

  if (!!!url) return messageComponent(PDFViewerConstants.LOADING_MESSAGE);

  return (
    <div ref={viewerRef} className={styles.pdfContainer}>
      <div className={styles.viewerWrapper}>
        <Document
          key={url}
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          className={PDFViewerConstants.DOC_COMPONENT_CLASS}
          error={messageComponent(PDFViewerConstants.ERROR_MESSAGE)}
          loading={messageComponent(PDFViewerConstants.LOADING_MESSAGE)}
        >
          {Array.from(new Array(numPages || 0), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              className={PDFViewerConstants.PAGE_COMPONENT_CLASS}
              scale={currentScale}
              canvasBackground={PDFViewerConstants.CANVAS_BACKGROUND_COLOR}
              onRenderSuccess={capturePageValues(index)}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      </div>
      <div className={styles.toolbarWrapper}>
        <PDFViewerToolbar
          currentPage={currentPage}
          numPages={1}
          currentScale={currentScale}
          currentViewMode={viewMode}
          fullscreen={fullscreen}
          onPageChange={setCurrentPage}
          onScaleChange={setCurrentScale}
          onViewModeChange={(viewMode: PdfViewMode) =>
            handleToggleViewMode(viewMode)
          }
          onFullscreenChange={toggleFullScreenView}
        />
      </div>
    </div>
  );
};

export default MainFileView;
